import groupBy from 'lodash/groupBy';
import { PortfolioAllocation, PortfolioAllocationDataPoint } from '@models';
import { sortPortfolioDataPoints } from '@products/portfolio/portfolio.utils';
import {
  PortfolioImpactPillarDTO,
  PortfolioComponentId,
  PortfolioDTO,
} from '@types';
import { formatPercentage } from '@utils/text/number-utils';
import { MapperFactory } from '../mapper-factory';
import { Mapper, MapperParams } from '../type.mapper';
import { Logger } from '@utils/logger';
import { getFirstNonEmptyProp, parseStdNumber, parseYN } from '../mapper.utils';

const logger = Logger.getLogger('ImpactPillarMapper');

export const isImpactPillarExposure = (
  point: PortfolioImpactPillarDTO
): boolean =>
  point.calcnamestd?.endsWith('_NET') || point.calcnamestd?.endsWith('_GROSS');

export const isImpactPillarAllocation = (
  point: PortfolioImpactPillarDTO
): boolean => !isImpactPillarExposure(point);

// tslint:disable-next-line:max-line-length
export const mapImpactPillarExposure = (
  data: PortfolioImpactPillarDTO[]
): PortfolioAllocation => {
  // this trims _NET or _GROSS off the end of the std category, so it can be grouped appropriately
  const grouper = (point: PortfolioImpactPillarDTO): string =>
    point.assetalloccatgstd?.replace(/_(NET|GROSS)$/g, '');

  const allocation: PortfolioAllocation = {
    componentId: PortfolioComponentId.IMPACT_PILLAR,
    baseLabelKey: 'impact-pillar',
    showExposureChart: true,
    allocationFlag: getFirstNonEmptyProp(data, 'allocflag'),
    allocationHeading: getFirstNonEmptyProp(data, 'allocheadinglocal'),
    asOfDate: getFirstNonEmptyProp(data, 'asofdate'),
    calculationBasis: getFirstNonEmptyProp(data, 'calcbasislocal'),
    calculationType: getFirstNonEmptyProp(data, 'calctypelocal'),
    hasDerivatives: parseYN(getFirstNonEmptyProp(data, 'hasderivatives')),
    updateFrequency: getFirstNonEmptyProp(data, 'frequency'),
    data: Object.values(groupBy(data, grouper))
      // after grouping, we'll get an array with [gross, net] pairs e.g. [[{gross}, {net}], [{net}, {gross}], [{gross}, {net}], ...]
      .map(
        (group: PortfolioImpactPillarDTO[]): PortfolioAllocationDataPoint => {
          // extract the gross and net data points for this category
          const netPoint: PortfolioImpactPillarDTO = group.find(
            (point: PortfolioImpactPillarDTO): boolean =>
              point.assetalloccatgstd?.endsWith('_NET')
          );
          const grossPoint: PortfolioImpactPillarDTO = group.find(
            (point: PortfolioImpactPillarDTO): boolean =>
              point.assetalloccatgstd?.endsWith('_GROSS')
          );
          // return a single point with gross+net values
          return {
            label: group[0].assetalloccatg,
            grossExposure: formatPercentage(grossPoint?.brkdwnpct),
            grossExposureStd: parseStdNumber(grossPoint?.brkdwnpctstd),
            netExposure: formatPercentage(netPoint?.brkdwnpct),
            netExposureStd: parseStdNumber(netPoint?.brkdwnpctstd),
            // work out this point's order based on the lowest "rank" property of the category
            order: Math.min(
              parseStdNumber(grossPoint?.rank),
              parseStdNumber(netPoint?.rank)
            ),
          };
        }
      )
      // sort the array
      .sort(
        (
          a: PortfolioAllocationDataPoint,
          b: PortfolioAllocationDataPoint
        ): number => a.order - b.order
      ),
  };
  return allocation;
};

/**
 * example fund:
 * https://staging.ftsites.com/en-gb/our-funds/price-and-performance
 * /products/36605/SG3/ftgf-martin-currie-improving-society-fund/IE000BDVR1N7#fund-portfolio
 */
export const mapImpactPillarAllocation = (
  data: PortfolioImpactPillarDTO[],
  mapperParams: MapperParams
): PortfolioAllocation => ({
  componentId: PortfolioComponentId.IMPACT_PILLAR,
  baseLabelKey: 'impact-pillar',
  showExposureChart: true,
  hideChart: true,
  allocationFlag: getFirstNonEmptyProp(data, 'allocflag'),
  allocationHeading: getFirstNonEmptyProp(data, 'allocheadinglocal'),
  asOfDate: getFirstNonEmptyProp(data, 'asofdate'),
  breakdownColumnTitle: mapperParams?.translateService.instant(
    'products.number-of-holdings'
  ),
  calculationBasis: getFirstNonEmptyProp(data, 'calcbasislocal'),
  calculationType: getFirstNonEmptyProp(data, 'calctypelocal'),
  hasDerivatives: parseYN(getFirstNonEmptyProp(data, 'hasderivatives')),
  updateFrequency: getFirstNonEmptyProp(data, 'frequency'),
  data: data.sort(sortPortfolioDataPoints).map(
    (obj: PortfolioImpactPillarDTO): PortfolioAllocationDataPoint => ({
      label: obj.assetalloccatg,
      breakdown: parseStdNumber(obj.brkdwnpct).toString(),
      breakdownStd: parseStdNumber(obj.brkdwnpctstd),
    })
  ),
});

export class ImpactPillarMapper extends Mapper<PortfolioAllocation[]> {
  constructor(mapperParams: MapperParams, mapperFactory: MapperFactory) {
    super(mapperParams, mapperFactory);
  }

  toDomain(portfolioDto: PortfolioDTO): PortfolioAllocation[] {
    const data: PortfolioImpactPillarDTO[] = portfolioDto.impactpillar;

    // if no data, return null
    if (!data || data.length === 0) {
      return null;
    }

    const allocations: PortfolioAllocation[] = [];

    const impactPillarExposureData: PortfolioImpactPillarDTO[] = data.filter(
      isImpactPillarExposure
    );
    if (impactPillarExposureData.length > 0) {
      allocations.push(mapImpactPillarExposure(impactPillarExposureData));
    }

    const impactPillarAllocationData: PortfolioImpactPillarDTO[] = data.filter(
      isImpactPillarAllocation
    );
    if (impactPillarAllocationData.length > 0) {
      allocations.push(
        mapImpactPillarAllocation(impactPillarAllocationData, this.mapperParams)
      );
    }

    return allocations;
  }
}
